import React, { useState } from "react";
import { useApp } from "@/context/AppContext";
import { Lightbox, LightboxAuthor, photoCaptionChars } from "@/components/PhotoGallery";
import { ReactionBar } from "@/components/ReactionBar";
import { AttnPulse } from "@/components/AttnPulse";
import { CommentModal } from "@/components/CommentModal";
import { AdStageCta } from "@/components/AdStageCta";
import { GuestCta } from "@/components/GuestCta";
import { PostLiveReactions } from "@/components/PostLiveReactions";
import { VideoPlayer } from "@/components/VideoPlayer";
import { BgTextFrame, BG_TEXT_PAD, BG_TEXT_SHADOW, BG_TEXT_MAX_PX } from "@/components/BgTextFrame";
import { AutoFitText } from "@/components/AutoFitText";
import { postIsBgText, postMediaItems } from "@/lib/postMedia";
import { EditableCaption } from "@/components/EditableCaption";
import { emitPostPatch } from "@/lib/postSync";

// Mobile photo viewer (Facebook-style): fullscreen photo, author + text (« Voir plus » after the
// admin-set number of characters) + J'aime / Commenter / Partager. « Commenter » shrinks the photo
// into the top band and slides the Reels-style comments sheet up. ✕ / back → previous screen.
// `index` follows the /post/:id?photo=N convention (album index when the post is an album).
// Album mêlant vidéos : on défile entre TOUS les médias ; une vidéo est lue avec le son (lecteur à contrôles).
// `initialComments` / `focusCommentId` : arrivée depuis « Commenter » ou une notification (?focus=comments&comment=…)
// → la feuille de commentaires est déjà ouverte (façon Instagram). `focusReactions` : la barre de réactions pulse.
export const PhotoViewerMobile = ({ post, index: initial = 0, onClose, initialComments = false, focusCommentId = null, focusReactions = false }) => {
  const { config, user } = useApp();
  // Carte texte sur fond coloré : même écran noir que les photos, le cadre 4:3 tient lieu de photo
  // (le texte est DANS le cadre → pas de légende en double).
  const bgText = postIsBgText(post);
  // Légendes modifiables depuis le téléphone : copie locale de l'album (mise à jour après enregistrement + patch du fil).
  const [album, setAlbum] = useState(post.album);
  const items = bgText ? [{ type: "text", url: "bg-text" }] : postMediaItems({ ...post, album });
  const images = items.map((m) => m.url);
  const [index, setIndex] = useState(Math.min(Math.max(0, initial), Math.max(0, items.length - 1)));
  const [comments, setComments] = useState(initialComments);
  const [commentCount, setCommentCount] = useState(post.comment_count || 0);
  const canEditCaption = !bgText && items.length > 1 && !!(post.is_owner || (user?.id && user.id === post.author_id) || ["super_admin", "moderator"].includes(user?.role));
  const saveCaption = (i, cap) => {
    const next = items.map((m, k) => (k === i ? { ...m, ...(cap ? { caption: cap } : {}) } : { ...m }));
    if (!cap) delete next[i].caption;
    setAlbum(next);
    emitPostPatch(post.id, { album: next });
  };
  if (items.length === 0) return null;
  const cur = items[Math.min(index, items.length - 1)];
  // Cadre borné à la hauteur de la bande (max-h-full) : clavier ouvert, la bande rétrécit et le texte se réajuste
  // dans un cadre moins haut au lieu de déborder sur la feuille de commentaires.
  const stage = bgText ? (
    <BgTextFrame bg={post.bg} className="w-full max-w-[560px] max-h-full" data-testid={`viewer-bg-${post.id}`}>
      <AutoFitText text={post.content} scale={post.text_scale || 1} maxPx={BG_TEXT_MAX_PX} boxClassName={BG_TEXT_PAD} className="font-bold text-white" style={BG_TEXT_SHADOW} testId={`viewer-bg-text-${post.id}`} />
    </BgTextFrame>
  ) : cur?.type === "video" ? (
    <div className="w-full h-full flex items-center justify-center" onClick={(e) => e.stopPropagation()} data-testid="album-viewer-video-stage">
      <VideoPlayer key={`${index}-${cur.url}`} mode="controls" src={cur.url} poster={cur.poster_url || undefined} width={cur.width || 0} height={cur.height || 0} fx={cur.fx || null} autoPlay adaptRatio maxHeight="100%"
        className="w-full rounded-lg" testId={`album-viewer-video-${index}`} fullscreenButton speedControl />
    </div>
  ) : null;
  return (
    <>
    <PostLiveReactions post={post} />
    <Lightbox
      images={images}
      index={index}
      onIndex={setIndex}
      onClose={onClose}
      author={<LightboxAuthor post={post} />}
      caption={bgText ? "" : (post.content || "")}
      mediaCaption={bgText ? "" : canEditCaption
        ? <EditableCaption key={`cap-${index}`} postId={post.id} mediaIndex={index} caption={cur?.caption || ""} canEdit dark onSaved={(c) => saveCaption(index, c)} testId="viewer-caption-editor" />
        : (cur?.caption || "")}
      stage={stage}
      shareLink={`/post/${post.id}`}
      downloadUrl={bgText ? null : (cur?.url || null)}
      downloadKind={cur?.type === "video" ? "video" : "image"}
      maxChars={photoCaptionChars(config)}
      actions={(
        <>
          {post.ad && <AdStageCta ad={post.ad} author={post.author} variant="card" />}
          {!user && <GuestCta compact dark testId="guest-cta-viewer" />}
          <AttnPulse active={focusReactions} testId={`viewer-reactions-${post.id}`}>
            <ReactionBar dark post={post} commentCount={commentCount} onCommentClick={() => setComments(true)} autoOpenReactors={focusReactions} />
          </AttnPulse>
        </>
      )}
      comments={comments ? (
        <CommentModal post={post} open embedded focusCommentId={focusCommentId} onOpenChange={(o) => { if (!o) setComments(false); }} onCountChange={(d) => setCommentCount((c) => Math.max(0, c + d))} />
      ) : null}
      onCommentsClose={() => setComments(false)}
    />
    </>
  );
};

export default PhotoViewerMobile;
